import { useState } from 'react';
import CopyRightIcon from '../../assets/icons/copy-right.svg?react';

interface CustomerContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer?: {
    email?: string;
    phoneNumber?: string;
  };
}

export const CustomerContactModal = ({ isOpen, onClose, customer }: CustomerContactModalProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const customerData = {
    email: customer?.email || 'No email available',
    phone: customer?.phoneNumber || 'No phone number available',
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleEmailClick = () => {
    if (customer?.email) {
      window.location.href = `mailto:${customer.email}`;
    }
  };

  const handlePhoneClick = () => {
    if (customer?.phoneNumber) {
      window.location.href = `tel:${customer.phoneNumber}`;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mt-1">
              <svg width="24" height="24" fill="none" viewBox="0 0 24 24" className="text-gray-600">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Customer contact</h3>
              <p className="text-sm text-gray-500 mt-1">
                Choose a contact method below and click on or copy them
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        
        <hr className="border-gray-200 mb-6" />

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <div className="relative">
              <input
                type="email"
                value={customerData.email}
                readOnly
                onClick={handleEmailClick}
                className={`w-full px-3 py-2 pr-10 border-0 bg-[#F8F9FB] rounded-lg text-gray-700 underline ${
                  customer?.email ? 'cursor-pointer' : ''
                }`}
                title={customer?.email ? 'Click to open email client' : ''}
              />
              <button
                onClick={() => handleCopy(customerData.email, 'email')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-600 hover:text-gray-800 transition-colors"
                title="Copy email"
              >
                {copiedField === 'email' ? (
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <CopyRightIcon width={20} height={20} />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone number
            </label>
            <div className="relative">
              <input
                type="tel"
                value={customerData.phone}
                readOnly
                onClick={handlePhoneClick}
                className={`w-full px-3 py-2 pr-10 border-0 bg-[#F8F9FB] rounded-lg text-gray-700 underline ${
                  customer?.phoneNumber ? 'cursor-pointer' : ''
                }`}
                title={customer?.phoneNumber ? 'Click to open dialer' : ''}
              />
              <button
                onClick={() => handleCopy(customerData.phone, 'phone')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-600 hover:text-gray-800 transition-colors"
                title="Copy phone number"
              >
                {copiedField === 'phone' ? (
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <CopyRightIcon width={20} height={20} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 